import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  message,
} from '@surecompanies/core_components'
import { Checkbox } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CensusIngestionChannel,
  useSaveCompanyIgnoreRulesMutation,
} from '../../api/company-ignore-api-client'
import ActiveConfigPanel from './ActiveConfigPanel'
import ChannelMultiSelect from './ChannelMultiSelect'
import FieldMultiSelect from './FieldMultiSelect'
import styles from './CompanyIgnore.module.scss'
import { formatDate } from './formatDate'
import { useCompanyIgnoreData } from './useCompanyIgnoreData'
import VersionHistory from './VersionHistory'

const ALL_CHANNELS = Object.values(CensusIngestionChannel)

interface CompanyIgnoreProps {
  clientPublicKey: string
  companyName: string
  embedded?: boolean
}

function getCompanyIgnoreErrorMessage(error: any): string {
  const data = error?.response?.data
  if (Array.isArray(data)) {
    return data
      .map((item) => {
        const errors = Array.isArray(item?.errors)
          ? item.errors.join(', ')
          : String(item?.errors ?? '')
        return item?.field ? `${item.field}: ${errors}` : errors
      })
      .filter(Boolean)
      .join('; ')
  }
  return data?.message ?? error?.message ?? 'Unknown error'
}

export default function CompanyIgnore({
  clientPublicKey,
  companyName,
  embedded = false,
}: CompanyIgnoreProps) {
  const [selectedFieldKeys, setSelectedFieldKeys] = useState<string[]>([])
  const [selectedChannels, setSelectedChannels] =
    useState<CensusIngestionChannel[]>(ALL_CHANNELS)
  const [changeReason, setChangeReason] = useState('')
  const [allowEligibilityImpactingFields, setAllowEligibilityImpactingFields] =
    useState(false)
  const [isFormHydrated, setIsFormHydrated] = useState(false)

  const {
    fields,
    activeConfig,
    versions,
    isLoading: isDataLoading,
    error,
    isHistoryLoading,
    historyError,
  } = useCompanyIgnoreData(clientPublicKey)

  const {
    data: savedActiveVersion,
    isPending: isSavePending,
    mutateAsync: saveCompanyIgnoreRules,
  } = useSaveCompanyIgnoreRulesMutation({
    onSuccess: (newActiveVersion) => {
      message.success(
        `Ignore changes rule v${newActiveVersion.version} is now active`
      )
      setChangeReason('')
    },
    onError: (error: any) => {
      const detail = getCompanyIgnoreErrorMessage(error)
      message.error(`Failed to save ignore changes rule: ${detail}`)
    },
  })

  // Sync before paint so Save cannot enable against empty defaults.
  // Company switches remount this component via key={clientPublicKey}.
  if (!isDataLoading && !error && !isFormHydrated) {
    const { ignoredFields = [], channels = ALL_CHANNELS } = activeConfig ?? {}
    setSelectedFieldKeys(ignoredFields)
    setSelectedChannels(channels)
    setIsFormHydrated(true)
  }

  const selectedFieldLabels = useMemo(() => {
    const fieldLabelByKey = new Map(
      fields.map((field) => [field.field, field.field])
    )
    return selectedFieldKeys.map(
      (fieldKey) => fieldLabelByKey.get(fieldKey) ?? fieldKey
    )
  }, [fields, selectedFieldKeys])

  const selectedEligibilityImpactingFields = useMemo(() => {
    const eligibilityFieldKeys = new Set(
      fields
        .filter((field) => field.isEligibilityImpacting)
        .map((field) => field.field)
    )
    return selectedFieldKeys.filter((fieldKey) =>
      eligibilityFieldKeys.has(fieldKey)
    )
  }, [fields, selectedFieldKeys])

  const hasEligibilityImpactingSelection =
    selectedEligibilityImpactingFields.length > 0

  useEffect(() => {
    if (!hasEligibilityImpactingSelection) {
      setAllowEligibilityImpactingFields(false)
    }
  }, [hasEligibilityImpactingSelection])

  const handleSave = useCallback(() => {
    if (!clientPublicKey) {
      message.error('Open a company before saving.')
      return
    }

    if (selectedChannels.length === 0) {
      message.error('Select at least one ingestion channel before saving.')
      return
    }

    Modal.confirm({
      title: 'Publish Ignore Changes Rule',
      content: (
        <div>
          <p>
            These fields will keep their current DB value for {companyName}, but
            only for the ingestion channels selected below — other channels can
            still update these fields.
          </p>
          <p>
            <strong>Ignored Fields:</strong>{' '}
            {selectedFieldLabels.length > 0
              ? selectedFieldLabels.join(', ')
              : 'No fields selected'}
          </p>
          {hasEligibilityImpactingSelection ? (
            <p>
              <strong>Eligibility-impacting fields:</strong>{' '}
              <strong style={{ color: '#d46b08' }}>
                {selectedEligibilityImpactingFields.join(', ')}
              </strong>
            </p>
          ) : null}
          <p>
            <strong>Channels:</strong> {selectedChannels.join(', ')}
          </p>
        </div>
      ),
      okText: 'Save and Publish',
      onOk: () =>
        saveCompanyIgnoreRules({
          clientPublicKey,
          body: {
            ignoredFields: selectedFieldKeys,
            channels: selectedChannels,
            ...(changeReason.trim()
              ? { changeReason: changeReason.trim() }
              : {}),
            ...(hasEligibilityImpactingSelection
              ? { allowEligibilityImpactingFields: true }
              : {}),
          },
        }),
    })
  }, [
    changeReason,
    clientPublicKey,
    companyName,
    hasEligibilityImpactingSelection,
    saveCompanyIgnoreRules,
    selectedChannels,
    selectedFieldKeys,
    selectedFieldLabels,
    selectedEligibilityImpactingFields,
  ])

  const isFormDisabled = !clientPublicKey || !isFormHydrated

  const formContent = (
    <>
      <div className={styles.formGridSingle}>
        <FieldMultiSelect
          fields={fields}
          value={selectedFieldKeys}
          loading={isDataLoading || !isFormHydrated}
          disabled={isFormDisabled}
          onChange={setSelectedFieldKeys}
        />
      </div>

      <div className={styles.formGridSingle} style={{ marginTop: 16 }}>
        <ChannelMultiSelect
          value={selectedChannels}
          disabled={isFormDisabled}
          onChange={setSelectedChannels}
        />
      </div>

      {hasEligibilityImpactingSelection ? (
        <Alert
          type="warning"
          showIcon
          message="Eligibility-impacting field selected"
          description={
            <>
              These field(s) can affect eligibility or pricing:
              {selectedEligibilityImpactingFields.map((field) => (
                <div key={field}>
                  <strong>{field}</strong>
                </div>
              ))}
              Confirm before publishing this ignore changes rule.
            </>
          }
          style={{ marginTop: 16 }}
        />
      ) : null}

      {hasEligibilityImpactingSelection ? (
        <div className={styles.confirmEligibility}>
          <Checkbox
            checked={allowEligibilityImpactingFields}
            disabled={isFormDisabled}
            onChange={(event) =>
              setAllowEligibilityImpactingFields(event.target.checked)
            }
          >
            I understand these fields impact eligibility/pricing and want to
            ignore them for this company.
          </Checkbox>
        </div>
      ) : null}

      <div className={styles.fieldGroup} style={{ marginTop: 20 }}>
        <label className={styles.label}>Change Reason (Optional)</label>
        <Input.TextArea
          value={changeReason}
          onChange={(event) => setChangeReason(event.target.value)}
          placeholder="Why is this ignore change rule changing?"
          rows={3}
          disabled={isFormDisabled || isSavePending}
        />
      </div>

      <div className={styles.actions} style={{ marginTop: 20 }}>
        <Button
          type="primary"
          onClick={handleSave}
          loading={isSavePending}
          disabled={
            !clientPublicKey ||
            !isFormHydrated ||
            isDataLoading ||
            selectedChannels.length === 0 ||
            (hasEligibilityImpactingSelection &&
              !allowEligibilityImpactingFields)
          }
        >
          Save
        </Button>
      </div>
    </>
  )

  return (
    <div
      className={
        embedded
          ? `${styles.companyIgnore} ${styles.companyIgnoreEmbedded}`
          : styles.companyIgnore
      }
    >
      <p className={styles.intro}>
        Configure ignore changes for fields that the backend mapper should
        ignore while preserving current CMv2 values across AMA, API, and manual
        uploads.
      </p>

      {error ? (
        <Alert
          type="error"
          showIcon
          message="Unable to load ignore changes data"
          description={(error as Error).message}
        />
      ) : null}

      {embedded ? formContent : <Card>{formContent}</Card>}

      {savedActiveVersion ? (
        <Alert
          type="success"
          showIcon
          message={`Active version v${savedActiveVersion.version}`}
          description={`Activated at ${formatDate(savedActiveVersion.activatedAt)}`}
        />
      ) : null}

      <ActiveConfigPanel
        activeConfig={activeConfig}
        fields={fields}
        loading={isDataLoading}
        error={error}
        asCard={!embedded}
      />

      <VersionHistory
        versions={versions}
        fields={fields}
        loading={isHistoryLoading}
        error={historyError}
        asCard={!embedded}
      />
    </div>
  )
}
